import { useEffect, useMemo, useState, type FormEvent } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Search as SearchIcon, SlidersHorizontal, Smartphone, UsersRound, X } from 'lucide-react';
import { Link, useNavigate, useSearch } from '@tanstack/react-router';
import {
  Avatar,
  Badge,
  Button,
  Card,
  CardBody,
  EmptyState,
  IconButton,
  Input,
  Label,
  Modal,
  Select,
  TD,
  TH,
  THead,
  TR,
  Table,
  Textarea,
  cn,
  PageHeader,
  useToast,
} from '@nexply/ui';
import type {
  MemberDTO,
  MemberCreateInput,
  MemberImportRow,
  ExtractedMember,
} from '@nexply/api-client';
import {
  daysUntilBirthday,
  parseCsvTable,
  autoMapColumns,
  buildRowsFromMapping,
  MEMBER_IMPORT_FIELDS,
  type MemberImportField,
} from '@nexply/core';
import { api } from '../../lib/api.js';
import { PhotoUpload } from '../../components/PhotoUpload.js';
import { InviteLinkCard } from '../../components/InviteLinkCard.js';
import { ContactsImportWizard } from '../../components/ContactsImportWizard.js';
import { ContactVoiceNote } from '../../components/ContactVoiceNote.js';
import { MemberFieldsManager, useMemberFields } from '../../components/MemberFieldsManager.js';
import { invalidateMemberViews } from '../../lib/queries.js';

type MemberStatus = MemberDTO['status'];
type Status = MemberStatus | '';
/** Eine Zeile der Ergebnisliste — mit Fundstellen, sobald gesucht wird. */
interface Row {
  member: MemberDTO;
  matchedFields?: string[];
  snippet?: string | null;
}

/** Deutsche Namen der Felder, in denen die Volltextsuche fündig wurde. */
const FIELD_LABEL: Record<string, string> = {
  name: 'Name',
  company: 'Firma',
  role: 'Position',
  industry: 'Branche',
  city: 'Stadt',
  country: 'Land',
  notes: 'Notizen',
  pitch: 'Pitch',
  offering: 'Bietet',
  seeking: 'Sucht',
  tags: 'Tags',
};

/** Deutsche Namen der Felder, die Nex per Sprachnotiz füllen kann. */
const AI_FIELD_LABEL: Record<string, string> = {
  company: 'Firma',
  role: 'Position',
  industry: 'Branche',
  city: 'Stadt',
  country: 'Land',
  email: 'E-Mail',
  phone: 'Telefon',
  website: 'Website',
  offering: 'Bietet an',
  seeking: 'Sucht',
  notes: 'Notizen',
  tags: 'Tags',
};

const BIRTHDAY_WINDOW_DAYS = 30;

const STATUS_LABEL: Record<MemberStatus, string> = {
  NEW: 'Neu',
  ACTIVE: 'Aktiv',
  ARCHIVED: 'Archiviert',
};

const STATUS_TONE: Record<MemberStatus, 'gold' | 'green' | 'neutral'> = {
  NEW: 'gold',
  ACTIVE: 'green',
  ARCHIVED: 'neutral',
};

const MONTHS = [
  'Januar',
  'Februar',
  'März',
  'April',
  'Mai',
  'Juni',
  'Juli',
  'August',
  'September',
  'Oktober',
  'November',
  'Dezember',
];

// "YYYY-MM-DD" niemals durch new Date(string) schicken: der Tag würde je nach Zeitzone
// verrutschen. Deshalb die Teile selbst zerlegen und in UTC zusammensetzen.
function birthdayParts(iso: string): { year: number; month: number; day: number } {
  const parts = iso.split('-');
  return { year: Number(parts[0]), month: Number(parts[1]), day: Number(parts[2]) };
}

function formatBirthday(iso: string): string {
  const { year, month, day } = birthdayParts(iso);
  return `${day}. ${MONTHS[month - 1]} ${year}`;
}

function birthdayCountdown(iso: string): string {
  const { year, month, day } = birthdayParts(iso);
  const days = daysUntilBirthday(new Date(Date.UTC(year, month - 1, day)));
  if (days === 0) return 'heute!';
  if (days === 1) return 'morgen';
  return `in ${days} Tagen`;
}

function useDebounced<T>(value: T, ms = 300): T {
  const [v, setV] = useState(value);
  useEffect(() => {
    const id = setTimeout(() => setV(value), ms);
    return () => clearTimeout(id);
  }, [value, ms]);
  return v;
}

export function MembersPage() {
  const qc = useQueryClient();
  const [search, setSearch] = useState('');
  const [tag, setTag] = useState<string>('');
  const [status, setStatus] = useState<Status>('');
  const [industry, setIndustry] = useState('');
  const [city, setCity] = useState('');
  const [hasOffering, setHasOffering] = useState(false);
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [birthdaysSoon, setBirthdaysSoon] = useState(false);
  const [openAdd, setOpenAdd] = useState(false);
  const [openImport, setOpenImport] = useState(false);
  const [openContacts, setOpenContacts] = useState(false);
  const [openFields, setOpenFields] = useState(false);
  const [detailId, setDetailId] = useState<string | null>(null);
  // Per Link geöffnet (/members?id=…): Detail aufmachen und den Parameter wieder aus der URL nehmen.
  const nav = useNavigate();
  const { id: linkedId } = useSearch({ strict: false }) as { id?: string };
  useEffect(() => {
    if (!linkedId) return;
    setDetailId(linkedId);
    nav({ to: '/members', search: {}, replace: true });
  }, [linkedId, nav]);

  const q = useDebounced(search, 300);
  const dIndustry = useDebounced(industry, 300);
  const dCity = useDebounced(city, 300);

  // Ein Suchfeld, zwei Quellen: ohne Suchbegriff die schlichte Liste, mit Suchbegriff
  // die Volltextsuche — die liest auch Pitch, Notizen, Angebot und Suche mit und sagt,
  // WO sie fündig wurde. Der Wechsel passiert lautlos, die Filterleiste bleibt dieselbe.
  const smart = !!(q.trim() || dIndustry.trim() || dCity.trim() || hasOffering);

  const list = useQuery({
    queryKey: ['members', { tag, status, birthdaysSoon }],
    queryFn: () =>
      api.members.list({
        tag: tag || undefined,
        status: status || undefined,
        birthdayWithinDays: birthdaysSoon ? BIRTHDAY_WINDOW_DAYS : undefined,
      }),
    enabled: !smart,
  });

  const found = useQuery({
    queryKey: ['search', { q, industry: dIndustry, city: dCity, hasOffering }],
    queryFn: () =>
      api.search.run({
        q: q.trim() || undefined,
        industry: dIndustry.trim() || undefined,
        city: dCity.trim() || undefined,
        hasOffering: hasOffering || undefined,
      }),
    enabled: smart,
  });

  // Tag, Status und Geburtstag kennt die Volltextsuche nicht — die filtert sie hier nach,
  // damit dieselbe Filterleiste in beiden Fällen gleich wirkt.
  const rows: Row[] = useMemo(() => {
    if (!smart) return (list.data?.members ?? []).map((member) => ({ member }));
    return (found.data?.results ?? [])
      .filter((r) => !tag || r.member.tags.includes(tag))
      .filter((r) => !status || r.member.status === status)
      .filter(
        (r) =>
          !birthdaysSoon ||
          (!!r.member.birthday &&
            daysUntilBirthday(new Date(r.member.birthday)) <= BIRTHDAY_WINDOW_DAYS),
      )
      .map((r) => ({ member: r.member, matchedFields: r.matchedFields, snippet: r.snippet }));
  }, [smart, list.data, found.data, tag, status, birthdaysSoon]);

  const loading = smart ? found.isLoading : list.isLoading;
  const advancedCount = [dIndustry.trim(), dCity.trim()].filter(Boolean).length + (hasOffering ? 1 : 0);
  const anyFilter = !!(tag || status || birthdaysSoon || advancedCount > 0 || search.trim());

  const resetFilters = () => {
    setSearch('');
    setTag('');
    setStatus('');
    setIndustry('');
    setCity('');
    setHasOffering(false);
    setBirthdaysSoon(false);
  };

  const tagsQ = useQuery({ queryKey: ['members', 'tags'], queryFn: () => api.members.tags() });

  const detail = useQuery({
    queryKey: ['members', detailId],
    queryFn: () => api.members.get(detailId!),
    enabled: !!detailId,
  });

  const invalidate = () => invalidateMemberViews(qc);

  const create = useMutation({
    mutationFn: (input: MemberCreateInput) => api.members.create(input),
    onSuccess: () => {
      invalidate();
      setOpenAdd(false);
    },
  });

  const update = useMutation({
    mutationFn: ({ id, input }: { id: string; input: MemberCreateInput }) =>
      api.members.update(id, input),
    onSuccess: () => {
      invalidate();
      setDetailId(null);
    },
  });

  const remove = useMutation({
    mutationFn: (id: string) => api.members.remove(id),
    onSuccess: () => {
      invalidate();
      setDetailId(null);
    },
  });

  const importMut = useMutation({
    mutationFn: (rows: MemberImportRow[]) => api.members.import(rows),
    onSuccess: () => invalidate(),
  });

  return (
    <div className="space-y-6 animate-slide-up">
      <PageHeader
        title="Mitglieder"
        subtitle={smart ? `${rows.length} Treffer` : `${rows.length} im Netzwerk`}
        actions={
          <>
            <Button
              variant="secondary"
              size="sm"
              onClick={() => setOpenContacts(true)}
              aria-label="Telefon-Kontakte einpflegen"
            >
              <Smartphone className="h-4 w-4" />
              {/* Auf dem Handy würden drei volle Beschriftungen umbrechen. */}
              <span className="hidden sm:inline">Telefon-Kontakte einpflegen</span>
              <span className="sm:hidden">Telefon</span>
            </Button>
            <Button variant="secondary" size="sm" onClick={() => setOpenImport(true)}>
              Import
            </Button>
            <Button
              variant="secondary"
              size="sm"
              onClick={() => setOpenFields(true)}
              aria-label="Eigene Felder verwalten"
            >
              <SlidersHorizontal className="h-4 w-4" />
              <span className="hidden sm:inline">Eigene Felder</span>
            </Button>
            <Button size="sm" onClick={() => setOpenAdd(true)}>+ Neu</Button>
          </>
        }
      />

      <InviteLinkCard />

      <Card>
        <CardBody className="space-y-3">
          <div className="relative">
            <SearchIcon className="pointer-events-none absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-text-muted" />
            <Input
              placeholder="Suchen — Name, Firma, Branche, Pitch, Angebot, Notizen …"
              value={search}
              onChange={(e) => setSearch(e.currentTarget.value)}
              className="h-12 pl-11 pr-11 text-base"
            />
            {search && (
              <button
                onClick={() => setSearch('')}
                aria-label="Suche leeren"
                className="absolute right-3 top-1/2 grid h-7 w-7 -translate-y-1/2 place-items-center rounded-full text-text-muted hover:bg-raise hover:text-text transition"
              >
                <X className="h-4 w-4" />
              </button>
            )}
          </div>

          <div className="grid grid-cols-2 gap-2 sm:grid-cols-[1fr_1fr_auto] sm:gap-3">
            <Select value={tag} onChange={(e) => setTag(e.currentTarget.value)}>
              <option value="">Alle Tags</option>
              {tagsQ.data?.tags.map((t) => (
                <option key={t} value={t}>
                  {t}
                </option>
              ))}
            </Select>
            <Select value={status} onChange={(e) => setStatus(e.currentTarget.value as Status)}>
              <option value="">Alle Status</option>
              <option value="NEW">Neu</option>
              <option value="ACTIVE">Aktiv</option>
              <option value="ARCHIVED">Archiviert</option>
            </Select>
            <button
              onClick={() => setShowAdvanced((v) => !v)}
              aria-expanded={showAdvanced}
              className={cn(
                'col-span-2 flex h-9 items-center justify-center gap-2 rounded-xl border px-3 text-sm transition sm:col-span-1',
                showAdvanced || advancedCount > 0
                  ? 'border-gold/40 bg-gold/10 text-gold-light'
                  : 'border-line text-text-dim hover:text-text hover:bg-raise',
              )}
            >
              <SlidersHorizontal className="h-4 w-4" />
              Erweitert
              {advancedCount > 0 && (
                <span className="rounded-full bg-gold/20 px-1.5 text-[10px] text-gold-light">
                  {advancedCount}
                </span>
              )}
            </button>
          </div>

          {showAdvanced && (
            <div className="grid grid-cols-1 gap-3 rounded-xl border border-line bg-surface-2 p-3 md:grid-cols-3">
              <div>
                <Label>Branche</Label>
                <Input
                  value={industry}
                  onChange={(e) => setIndustry(e.currentTarget.value)}
                  placeholder="z. B. IT-Beratung"
                />
              </div>
              <div>
                <Label>Stadt</Label>
                <Input
                  value={city}
                  onChange={(e) => setCity(e.currentTarget.value)}
                  placeholder="z. B. Berlin"
                />
              </div>
              <div>
                <Label>Nur mit Angebot</Label>
                <label className="flex h-9 cursor-pointer items-center gap-2 text-sm text-text-dim">
                  <input
                    type="checkbox"
                    checked={hasOffering}
                    onChange={(e) => setHasOffering(e.currentTarget.checked)}
                    className="accent-gold"
                  />
                  Hat ein Angebot hinterlegt
                </label>
              </div>
            </div>
          )}

          <div className="flex flex-wrap items-center gap-1.5">
            <button
              onClick={() => setBirthdaysSoon((v) => !v)}
              className={cn(
                'text-[11px] rounded-full px-2.5 py-1 border transition',
                birthdaysSoon
                  ? 'border-gold/40 bg-gold/10 text-gold-light'
                  : 'border-line text-text-muted hover:text-text',
              )}
            >
              🎂 Bevorstehende Geburtstage
            </button>
            {tagsQ.data?.tags.slice(0, 12).map((t) => (
              <button
                key={t}
                onClick={() => setTag(t === tag ? '' : t)}
                className={cn(
                  'text-[11px] rounded-full px-2.5 py-1 border transition',
                  t === tag
                    ? 'border-gold/40 bg-gold/10 text-gold-light'
                    : 'border-line text-text-muted hover:text-text',
                )}
              >
                {t}
              </button>
            ))}
            {anyFilter && (
              <button
                onClick={resetFilters}
                className="ml-auto text-[11px] text-text-muted underline-offset-2 hover:text-text hover:underline"
              >
                Alles zurücksetzen
              </button>
            )}
          </div>
        </CardBody>
      </Card>

      {/* Die Suche greift weiter als die Liste — das darf man auch sehen. */}
      {smart && !loading && rows.length > 0 && (
        <div className="text-xs text-text-muted">
          {rows.length} {rows.length === 1 ? 'Treffer' : 'Treffer'} · Volltextsuche über alle Profilfelder
        </div>
      )}

      {loading && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
          {Array.from({ length: 6 }).map((_, i) => (
            <div
              key={i}
              className="h-32 rounded-xl border border-line bg-raise animate-pulse"
            />
          ))}
        </div>
      )}

      {!loading && rows.length === 0 && smart && (
        <EmptyState
          icon="∅"
          title="Keine Treffer"
          description="Passe Suchbegriff oder Filter an."
          action={<Button variant="secondary" onClick={resetFilters}>Filter zurücksetzen</Button>}
        />
      )}

      {!loading && rows.length === 0 && !smart && (
        <EmptyState
          mascot="idle"
          title={anyFilter ? 'Nichts gefunden' : 'Noch keine Mitglieder'}
          description={
            anyFilter
              ? 'Mit diesen Filtern ist niemand im Netzwerk.'
              : 'Lege dein erstes Mitglied an oder importiere eine CSV-Datei.'
          }
          action={
            anyFilter ? (
              <Button variant="secondary" onClick={resetFilters}>Filter zurücksetzen</Button>
            ) : (
              <Button onClick={() => setOpenAdd(true)}>Mitglied hinzufügen</Button>
            )
          }
        />
      )}

      {!loading && rows.length > 0 && (
        <div className="space-y-2 md:space-y-0 md:grid md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 md:gap-3">
          {rows.map((r) => (
            <MemberRow
              key={r.member.id}
              member={r.member}
              showBirthday={birthdaysSoon}
              matchedFields={r.matchedFields}
              snippet={r.snippet}
              onClick={() => setDetailId(r.member.id)}
            />
          ))}
        </div>
      )}

      <AddOrEditModal
        open={openAdd}
        onClose={() => setOpenAdd(false)}
        onSubmit={(input) => create.mutate(input)}
        pending={create.isPending}
        error={
          create.isError
            ? create.error instanceof Error && create.error.message !== 'invalid_input'
              ? create.error.message
              : 'Anlegen fehlgeschlagen — bitte Eingaben prüfen.'
            : null
        }
        title="Mitglied hinzufügen"
      />

      <ImportModal
        open={openImport}
        onClose={() => setOpenImport(false)}
        onSubmit={(rows) => importMut.mutate(rows)}
        result={importMut.data}
        pending={importMut.isPending}
      />

      {/* Der Einpflege-Assistent ist bewusst ein Vollbild-Popup: er hat eigene
          Schritte und soll die Mitgliederliste dahinter nicht ersetzen. */}
      <Modal
        open={openContacts}
        onClose={() => setOpenContacts(false)}
        variant="full"
        title="Telefon-Kontakte einpflegen"
        description="Kontakte vom Handy durchgehen — rechts übernehmen, links verwerfen."
      >
        <ContactsImportWizard onClose={() => setOpenContacts(false)} />
      </Modal>

      <MemberFieldsManager open={openFields} onClose={() => setOpenFields(false)} />

      {detailId && (
        <DetailModal
          open={!!detailId}
          onClose={() => setDetailId(null)}
          member={detail.data?.member ?? null}
          onUpdate={(input) => update.mutate({ id: detailId, input })}
          onDelete={() => {
            if (confirm('Mitglied wirklich löschen?')) remove.mutate(detailId);
          }}
          pending={update.isPending || remove.isPending}
          error={
            update.isError
              ? update.error instanceof Error && update.error.message !== 'invalid_input'
                ? update.error.message
                : 'Speichern fehlgeschlagen — bitte Eingaben prüfen.'
              : null
          }
        />
      )}
    </div>
  );
}

function MemberRow({
  member,
  showBirthday,
  matchedFields,
  snippet,
  onClick,
}: {
  member: MemberDTO;
  showBirthday: boolean;
  /** Nur bei aktiver Suche gesetzt: in welchen Feldern der Begriff steckt. */
  matchedFields?: string[];
  snippet?: string | null;
  onClick: () => void;
}) {
  return (
    <button
      onClick={onClick}
      className="group flex w-full items-center gap-3 rounded-2xl border border-line bg-surface-2 px-4 py-3 text-left transition-all active:scale-[0.985] hover:border-gold/20 hover:bg-surface-3"
    >
      <Avatar name={member.name} src={member.photoUrl} size="md" className="shrink-0" />
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <div className="text-[15px] font-medium text-text truncate">{member.name}</div>
          {member.status !== 'ACTIVE' && (
            <Badge tone={STATUS_TONE[member.status]}>{STATUS_LABEL[member.status]}</Badge>
          )}
        </div>
        <div className="mt-0.5 text-xs text-text-dim truncate">
          {[member.role, member.company, member.industry].filter(Boolean).join(' · ') || (member.city ?? 'Mitglied')}
        </div>
        {member.connectedViaGroup && (
          <div className="mt-0.5 flex items-center gap-1 text-[11px] text-gold-light/80 truncate">
            <UsersRound className="h-3 w-3 shrink-0" />
            <span className="truncate">Vernetzt durch Gruppe „{member.connectedViaGroup.name}“</span>
          </div>
        )}
        {showBirthday && member.birthday && (
          <div className="mt-0.5 text-xs text-gold-light">
            🎂 {formatBirthday(member.birthday)} · {birthdayCountdown(member.birthday)}
          </div>
        )}
        {snippet && (
          <div className="mt-1.5 text-xs text-text-muted line-clamp-2">{snippet}</div>
        )}
        {matchedFields && matchedFields.length > 0 && (
          <div className="mt-1.5 flex flex-wrap gap-1">
            {matchedFields.slice(0, 4).map((f) => (
              <span
                key={f}
                className="rounded-full border border-success/25 bg-success/10 px-2 py-0.5 text-[10px] text-success"
              >
                {FIELD_LABEL[f] ?? f}
              </span>
            ))}
          </div>
        )}
        {!matchedFields && member.tags.length > 0 && (
          <div className="mt-1.5 flex flex-wrap gap-1">
            {member.tags.slice(0, 3).map((t) => (
              <span key={t} className="rounded-full bg-gold/[0.06] border border-gold/15 px-2 py-0.5 text-[10px] text-gold-light">
                {t}
              </span>
            ))}
          </div>
        )}
      </div>
      <svg className="shrink-0 h-5 w-5 text-text-muted group-hover:text-gold transition" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.5">
        <path d="m8 5 5 5-5 5" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    </button>
  );
}

/** Die eigenen Felder in der Detailansicht — nur die, die bei dieser Person gefüllt sind. */
function CustomFieldValues({ member }: { member: MemberDTO }) {
  const { data } = useMemberFields();
  const defs = (data?.fields ?? []).filter((d) => {
    const v = member.customFields[d.key];
    return v !== undefined && v !== '' && v !== false;
  });
  if (defs.length === 0) return null;

  return (
    <div className="rounded-2xl border border-line bg-raise p-4">
      <div className="mb-2 text-xs font-medium uppercase tracking-wider text-text-muted">
        Eigene Felder
      </div>
      <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-2">
        {defs.map((d) => {
          const raw = member.customFields[d.key];
          const value =
            d.type === 'CHECKBOX'
              ? 'Ja'
              : d.type === 'DATE' && typeof raw === 'string'
                ? new Date(`${raw}T00:00:00Z`).toLocaleDateString('de-DE', { timeZone: 'UTC' })
                : String(raw);
          return (
            <div key={d.id} className="min-w-0">
              <dt className="text-[11px] text-text-muted">{d.label}</dt>
              <dd className="text-sm text-text break-words">{value}</dd>
            </div>
          );
        })}
      </dl>
    </div>
  );
}

interface FormProps {
  initial?: MemberDTO | null;
  onSubmit: (input: MemberCreateInput) => void;
  onCancel?: () => void;
  submitLabel?: string;
  pending?: boolean;
  /** Fehlermeldung der letzten Mutation — ohne sie wirkt ein fehlgeschlagenes Speichern wie ein toter Knopf. */
  error?: string | null;
}

function MemberForm({ initial, onSubmit, onCancel, submitLabel = 'Speichern', pending, error }: FormProps) {
  const [form, setForm] = useState({
    firstName: initial?.firstName ?? '',
    lastName: initial?.lastName ?? '',
    company: initial?.company ?? '',
    role: initial?.role ?? '',
    industry: initial?.industry ?? '',
    email: initial?.email ?? '',
    phone: initial?.phone ?? '',
    website: initial?.website ?? '',
    linkedin: initial?.linkedin ?? '',
    instagram: initial?.instagram ?? '',
    xing: initial?.xing ?? '',
    street: initial?.street ?? '',
    zip: initial?.zip ?? '',
    city: initial?.city ?? '',
    country: initial?.country ?? '',
    birthday: initial?.birthday ?? '',
    tagsRaw: (initial?.tags ?? []).join(', '),
    notes: initial?.notes ?? '',
    status: initial?.status ?? 'ACTIVE',
    photoUrl: initial?.photoUrl ?? '',
    pitch: initial?.pitch ?? '',
    offering: initial?.offering ?? '',
    seeking: initial?.seeking ?? '',
  });

  function submit(e: FormEvent) {
    e.preventDefault();
    const tags = form.tagsRaw
      .split(',')
      .map((t) => t.trim())
      .filter(Boolean);
    onSubmit({
      firstName: form.firstName.trim(),
      lastName: form.lastName.trim() || null,
      company: form.company || null,
      role: form.role || null,
      industry: form.industry || null,
      email: form.email || null,
      phone: form.phone || null,
      website: form.website || null,
      linkedin: form.linkedin || null,
      instagram: form.instagram || null,
      xing: form.xing || null,
      street: form.street || null,
      zip: form.zip || null,
      city: form.city || null,
      country: form.country || null,
      birthday: form.birthday || null,
      tags,
      notes: form.notes || null,
      status: form.status as MemberStatus,
      photoUrl: form.photoUrl || null,
      pitch: form.pitch || null,
      offering: form.offering || null,
      seeking: form.seeking || null,
      // Immer ALLE definierten Felder mitschicken: Ein leer geräumtes Feld wird
      // dadurch auch wirklich gelöscht und nicht nur nicht erwähnt.
      customFields: Object.fromEntries(
        customDefs.map((d) => [d.key, custom[d.key] ?? '']),
      ),
    });
  }

  /** Welche Felder Nex zuletzt ergänzt hat — nur für den Hinweis über dem Formular. */
  const [aiFields, setAiFields] = useState<Set<string>>(new Set());

  // Eigene Felder des Nutzers. Werte als Strings im Formular — die Umwandlung in
  // Zahl/Boolean macht der Server anhand der Felddefinition.
  const fieldsQ = useMemberFields();
  const customDefs = fieldsQ.data?.fields ?? [];
  const [custom, setCustom] = useState<Record<string, string>>(() => {
    const out: Record<string, string> = {};
    for (const [k, v] of Object.entries(initial?.customFields ?? {})) out[k] = String(v);
    return out;
  });
  const updCustom = (key: string, value: string) =>
    setCustom((c) => ({ ...c, [key]: value }));

  const upd = <K extends keyof typeof form>(k: K, v: (typeof form)[K]) => {
    setForm((f) => ({ ...f, [k]: v }));
    // Selbst getipptes gehört dem Nutzer — die KI-Markierung fällt weg.
    setAiFields((prev) => {
      if (!prev.has(k as string)) return prev;
      const next = new Set(prev);
      next.delete(k as string);
      return next;
    });
  };

  /**
   * KI-Ergebnis ins Formular übernehmen. Zwei Regeln, damit während des Sprechens
   * nichts verloren geht:
   * - Nur LEERE Felder werden gefüllt — was der Nutzer (oder der bestehende
   *   Kontakt) schon hat, bleibt unangetastet.
   * - Notizen werden angehängt, Tags vereinigt.
   * Als funktionaler Updater: der Nutzer darf während des KI-Aufrufs weitertippen.
   */
  function applyExtracted(ex: ExtractedMember) {
    const marked = new Set<string>();

    setForm((current) => {
      const next = { ...current };
      const put = (key: 'company' | 'role' | 'industry' | 'city' | 'country' | 'email' | 'phone' | 'website' | 'offering' | 'seeking', value: string | null) => {
        const v = (value ?? '').trim();
        if (!v || next[key].trim()) return;
        next[key] = v;
        marked.add(key);
      };

      put('company', ex.company);
      put('role', ex.role);
      put('industry', ex.industry);
      put('city', ex.city);
      put('country', ex.country);
      put('email', ex.email);
      put('phone', ex.phone);
      put('website', ex.website);
      put('offering', ex.offering);
      put('seeking', ex.seeking);

      const note = (ex.notes ?? '').trim();
      if (note && !next.notes.includes(note)) {
        next.notes = next.notes.trim() ? `${next.notes.trim()}\n\n${note}` : note;
        marked.add('notes');
      }

      const existing = next.tagsRaw.split(',').map((t) => t.trim()).filter(Boolean);
      const seen = new Set(existing.map((t) => t.toLowerCase()));
      const addedTags = ex.tags.map((t) => t.trim()).filter((t) => t && !seen.has(t.toLowerCase()));
      if (addedTags.length > 0) {
        next.tagsRaw = [...existing, ...addedTags].join(', ');
        marked.add('tags');
      }

      return next;
    });

    setAiFields((prev) => new Set([...prev, ...marked]));
  }

  return (
    <form onSubmit={submit} className="space-y-5">
      <ContactVoiceNote
        active
        live
        applied={aiFields.size > 0}
        known={{
          name: `${form.firstName} ${form.lastName}`.trim() || undefined,
          company: form.company.trim() || null,
          role: form.role.trim() || null,
          city: form.city.trim() || null,
        }}
        onExtracted={applyExtracted}
      />

      {aiFields.size > 0 && (
        <div className="rounded-xl border border-gold/25 bg-gold/[0.06] px-3 py-2 text-xs text-text-dim">
          Nex hat {aiFields.size} {aiFields.size === 1 ? 'Feld' : 'Felder'} ergänzt (
          {[...aiFields].map((f) => AI_FIELD_LABEL[f] ?? f).join(', ')}) — alles bleibt änderbar.
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <div>
          <Label>Vorname *</Label>
          <Input value={form.firstName} onChange={(e) => upd('firstName', e.currentTarget.value)} autoComplete="given-name" required />
        </div>
        <div>
          <Label>Nachname</Label>
          <Input value={form.lastName} onChange={(e) => upd('lastName', e.currentTarget.value)} autoComplete="family-name" />
        </div>
        <div>
          <Label>Firma</Label>
          <Input value={form.company} onChange={(e) => upd('company', e.currentTarget.value)} />
        </div>
        <div>
          <Label>Position / Rolle</Label>
          <Input value={form.role} onChange={(e) => upd('role', e.currentTarget.value)} />
        </div>
        <div>
          <Label>Branche</Label>
          <Input value={form.industry} onChange={(e) => upd('industry', e.currentTarget.value)} />
        </div>
        <div>
          <Label>E-Mail</Label>
          <Input
            type="email"
            value={form.email}
            onChange={(e) => upd('email', e.currentTarget.value)}
          />
        </div>
        <div>
          <Label>Telefon</Label>
          <Input value={form.phone} onChange={(e) => upd('phone', e.currentTarget.value)} />
        </div>
        <div>
          <Label>Website</Label>
          <Input value={form.website} onChange={(e) => upd('website', e.currentTarget.value)} />
        </div>
        <div>
          <Label>LinkedIn</Label>
          <Input value={form.linkedin} onChange={(e) => upd('linkedin', e.currentTarget.value)} />
        </div>
        <div>
          <Label>Instagram</Label>
          <Input value={form.instagram} onChange={(e) => upd('instagram', e.currentTarget.value)} />
        </div>
        <div>
          <Label>XING</Label>
          <Input value={form.xing} onChange={(e) => upd('xing', e.currentTarget.value)} />
        </div>
        <div>
          <Label>Straße &amp; Hausnummer</Label>
          <Input value={form.street} onChange={(e) => upd('street', e.currentTarget.value)} autoComplete="street-address" />
        </div>
        <div>
          <Label>PLZ</Label>
          <Input value={form.zip} onChange={(e) => upd('zip', e.currentTarget.value)} autoComplete="postal-code" />
        </div>
        <div>
          <Label>Stadt</Label>
          <Input value={form.city} onChange={(e) => upd('city', e.currentTarget.value)} />
        </div>
        <div>
          <Label>Land</Label>
          <Input value={form.country} onChange={(e) => upd('country', e.currentTarget.value)} />
        </div>
        <div>
          <Label>Geburtstag</Label>
          <Input
            type="date"
            value={form.birthday}
            onChange={(e) => upd('birthday', e.currentTarget.value)}
          />
        </div>
        <div className="md:col-span-2">
          <Label>Tags (kommagetrennt)</Label>
          <Input
            value={form.tagsRaw}
            onChange={(e) => upd('tagsRaw', e.currentTarget.value)}
            placeholder="z.B. KI, B2B, Coaching"
          />
        </div>
        <div className="md:col-span-2">
          <Label>Foto</Label>
          <PhotoUpload
            value={form.photoUrl}
            onChange={(v) => upd('photoUrl', v)}
            fallbackInitials={((form.firstName[0] ?? '') + (form.lastName[0] ?? '') || '?').toUpperCase()}
          />
        </div>
        <div>
          <Label>Status</Label>
          <Select value={form.status} onChange={(e) => upd('status', e.currentTarget.value as MemberStatus)}>
            <option value="NEW">Neu</option>
            <option value="ACTIVE">Aktiv</option>
            <option value="ARCHIVED">Archiviert</option>
          </Select>
        </div>
      </div>

      <div className="space-y-3">
        <div>
          <Label>Notizen</Label>
          <Textarea value={form.notes} onChange={(e) => upd('notes', e.currentTarget.value)} />
        </div>
        <div>
          <Label>Pitch</Label>
          <Textarea value={form.pitch} onChange={(e) => upd('pitch', e.currentTarget.value)} />
        </div>
        <div>
          <Label>Bietet an</Label>
          <Textarea value={form.offering} onChange={(e) => upd('offering', e.currentTarget.value)} />
        </div>
        <div>
          <Label>Sucht</Label>
          <Textarea value={form.seeking} onChange={(e) => upd('seeking', e.currentTarget.value)} />
        </div>
      </div>

      {customDefs.length > 0 && (
        <div className="space-y-3 rounded-2xl border border-line bg-raise p-4">
          <div className="text-xs font-medium uppercase tracking-wider text-text-muted">
            Eigene Felder
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {customDefs.map((def) => (
              <div key={def.id} className={def.type === 'LONGTEXT' ? 'md:col-span-2' : undefined}>
                <Label>{def.label}</Label>
                {def.type === 'LONGTEXT' ? (
                  <Textarea
                    value={custom[def.key] ?? ''}
                    onChange={(e) => updCustom(def.key, e.currentTarget.value)}
                  />
                ) : def.type === 'SELECT' ? (
                  <Select
                    value={custom[def.key] ?? ''}
                    onChange={(e) => updCustom(def.key, e.currentTarget.value)}
                  >
                    <option value="">— keine Angabe —</option>
                    {def.options.map((o) => (
                      <option key={o} value={o}>
                        {o}
                      </option>
                    ))}
                  </Select>
                ) : def.type === 'CHECKBOX' ? (
                  <label className="flex h-12 cursor-pointer items-center gap-2 rounded-xl border border-white/[0.06] bg-surface-2 px-4 text-sm text-text-dim">
                    <input
                      type="checkbox"
                      checked={custom[def.key] === 'true'}
                      onChange={(e) => updCustom(def.key, e.currentTarget.checked ? 'true' : '')}
                      className="h-4 w-4 accent-gold"
                    />
                    Ja
                  </label>
                ) : (
                  <Input
                    type={def.type === 'NUMBER' ? 'number' : def.type === 'DATE' ? 'date' : 'text'}
                    value={custom[def.key] ?? ''}
                    onChange={(e) => updCustom(def.key, e.currentTarget.value)}
                  />
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="flex items-center justify-end gap-3 pt-2">
        {error && <span className="text-xs text-danger">{error}</span>}
        {onCancel && (
          <Button type="button" variant="ghost" onClick={onCancel}>
            Abbrechen
          </Button>
        )}
        <Button type="submit" disabled={pending || !form.firstName.trim()}>
          {submitLabel}
        </Button>
      </div>
    </form>
  );
}

function AddOrEditModal({
  open,
  onClose,
  onSubmit,
  pending,
  error,
  title,
}: {
  open: boolean;
  onClose: () => void;
  onSubmit: (input: MemberCreateInput) => void;
  pending: boolean;
  error?: string | null;
  title: string;
}) {
  return (
    <Modal open={open} onClose={onClose} title={title} size="lg">
      <MemberForm
        onSubmit={(input) => onSubmit(input)}
        onCancel={onClose}
        submitLabel="Anlegen"
        pending={pending}
        error={error}
      />
    </Modal>
  );
}

function DetailModal({
  open,
  onClose,
  member,
  onUpdate,
  onDelete,
  pending,
  error,
}: {
  open: boolean;
  onClose: () => void;
  member: MemberDTO | null;
  onUpdate: (input: MemberCreateInput) => void;
  onDelete: () => void;
  pending: boolean;
  error?: string | null;
}) {
  const [edit, setEdit] = useState(false);
  useEffect(() => {
    if (!open) setEdit(false);
  }, [open]);

  return (
    <Modal open={open} onClose={onClose} title={member?.name ?? 'Mitglied'} size="lg">
      {!member && <div className="text-sm text-text-muted">Lädt…</div>}
      {member && !edit && (
        <div className="space-y-4">
          <div className="flex items-start gap-4">
            <Avatar name={member.name} src={member.photoUrl} size="xl" className="shrink-0" />
            <div className="flex flex-wrap gap-1.5">
              {member.industry && <Badge tone="gold">{member.industry}</Badge>}
              <Badge tone={STATUS_TONE[member.status]}>{STATUS_LABEL[member.status]}</Badge>
              {member.tags.map((t) => (
                <Badge key={t}>{t}</Badge>
              ))}
            </div>
          </div>

          {member.connectedViaGroup && (
            <Link
              to="/groups/$groupId"
              params={{ groupId: member.connectedViaGroup.id }}
              className="flex items-center gap-2 rounded-xl border border-gold/15 bg-gold/[0.06] px-3 py-2 text-sm text-gold-light hover:bg-gold/10"
            >
              <UsersRound className="h-4 w-4 shrink-0" />
              <span className="truncate">Vernetzt durch Gruppe „{member.connectedViaGroup.name}“</span>
            </Link>
          )}

          <DetailGrid member={member} />

          <CustomFieldValues member={member} />

          {member.notes && <DetailBlock label="Notizen" text={member.notes} />}
          {member.pitch && <DetailBlock label="Pitch" text={member.pitch} />}
          {member.offering && <DetailBlock label="Bietet an" text={member.offering} />}
          {member.seeking && <DetailBlock label="Sucht" text={member.seeking} />}

          <div className="flex justify-end gap-2 pt-3 border-t border-line">
            <Button variant="danger" onClick={onDelete} disabled={pending}>
              Löschen
            </Button>
            <Button variant="ghost" onClick={() => setEdit(true)}>
              Bearbeiten
            </Button>
          </div>
        </div>
      )}
      {member && edit && (
        <MemberForm
          initial={member}
          // Nicht optimistisch schließen: das Fenster geht erst zu, wenn die Mutation
          // erfolgreich war (update.onSuccess → setDetailId(null)). Sonst landet man bei
          // einem fehlgeschlagenen PATCH stumm wieder auf den alten Daten.
          onSubmit={(input) => onUpdate(input)}
          onCancel={onClose}
          submitLabel="Aktualisieren"
          pending={pending}
          error={error}
        />
      )}
    </Modal>
  );
}

function DetailBlock({ label, text }: { label: string; text: string }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-wider text-text-muted mb-1">{label}</div>
      <div className="text-sm text-text-dim whitespace-pre-wrap">{text}</div>
    </div>
  );
}

function DetailGrid({ member }: { member: MemberDTO }) {
  const rows: Array<[string, string | null]> = [
    ['Firma', member.company],
    ['Position', member.role],
    ['E-Mail', member.email],
    ['Telefon', member.phone],
    ['Website', member.website],
    ['LinkedIn', member.linkedin],
    ['Instagram', member.instagram],
    ['XING', member.xing],
    ['Adresse', [member.street, [member.zip, member.city].filter(Boolean).join(' ')].filter(Boolean).join(', ') || null],
    ['Land', member.country],
    ['Geburtstag', member.birthday ? formatBirthday(member.birthday) : null],
  ];
  const filled = rows.filter(([, v]) => v);
  if (filled.length === 0) return null;
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-2 text-sm">
      {filled.map(([k, v]) => (
        <div key={k} className="flex justify-between gap-3 border-b border-line pb-1">
          <span className="text-text-muted">{k}</span>
          <span className="text-text-dim text-right truncate" title={v ?? ''}>
            {v}
          </span>
        </div>
      ))}
    </div>
  );
}

/**
 * Fehlermeldung zu einem gescheiterten KI-Aufruf. Die Unterscheidung ist
 * wichtig: "nicht eingerichtet" kann der Nutzer nicht durch Nochmal-Klicken
 * beheben — ein pauschales "fehlgeschlagen" schickt ihn genau dahin.
 */
function aiErrorMessage(e: unknown, fallback: string): string {
  const code = (e as { code?: string } | null)?.code;
  if (code === 'ai_unavailable') {
    return `KI ist auf diesem Server nicht eingerichtet (kein API-Schlüssel hinterlegt) — ${fallback}`;
  }
  if (code === 'ai_failed') {
    return `Die KI-Antwort war unbrauchbar — probier es nochmal oder ${fallback}`;
  }
  return `KI-Aufruf fehlgeschlagen — ${fallback}`;
}

/**
 * Import-Assistent. Zwei Wege in dasselbe Vorschau→Import-Finale:
 * - CSV/Datei: Struktur wird automatisch erkannt (deutsche/englische Header + Presets
 *   für HubSpot, Salesforce, Pipedrive, Outlook, Google Kontakte). Was nicht passt,
 *   ordnet auf Wunsch die KI zu — und jede Spalte bleibt von Hand korrigierbar.
 * - Freitext: E-Mail-Signaturen, kopierte Listen o.ä. — die KI extrahiert Kontakte.
 */
function ImportModal({
  open,
  onClose,
  onSubmit,
  result,
  pending,
}: {
  open: boolean;
  onClose: () => void;
  onSubmit: (rows: MemberImportRow[]) => void;
  result?: { created: number; skipped: number; errors: { index: number; error: string }[] };
  pending: boolean;
}) {
  const [tab, setTab] = useState<'csv' | 'text'>('csv');

  // --- CSV-Weg ---
  const [csv, setCsv] = useState('');
  const table = useMemo(() => (csv.trim() ? parseCsvTable(csv) : null), [csv]);
  const [mapping, setMapping] = useState<(MemberImportField | null)[]>([]);
  const [aiMap, setAiMap] = useState<{ pending: boolean; error: string | null; done: boolean }>({
    pending: false,
    error: null,
    done: false,
  });

  // Neue Datei/neuer Text → Mapping frisch automatisch bestimmen.
  useEffect(() => {
    setMapping(table ? autoMapColumns(table.header) : []);
    setAiMap({ pending: false, error: null, done: false });
  }, [table]);

  const built = useMemo(
    () => (table ? buildRowsFromMapping(table, mapping) : { rows: [], errors: [] }),
    [table, mapping],
  );

  const unmappedCount = table ? mapping.filter((m) => m === null).length : 0;
  const hasNameMapping =
    mapping.includes('name') || mapping.includes('firstName') || mapping.includes('lastName');

  async function runAiMapping() {
    if (!table) return;
    setAiMap({ pending: true, error: null, done: false });
    try {
      const res = await api.members.importMap({
        // Nur ein Anriss je Zelle: Die KI soll sehen, wie eine Spalte aussieht,
        // nicht den ganzen Inhalt lesen. Lange Notiz-Zellen aus CRM-Exporten
        // haben die Zuordnung sonst komplett scheitern lassen.
        header: table.header.map((h) => h.slice(0, 200)).slice(0, 200),
        sample: table.rows
          .slice(0, 5)
          .map((row) => row.slice(0, 200).map((cell) => cell.slice(0, 200))),
      });
      const valid = new Set<string>(MEMBER_IMPORT_FIELDS.map((f) => f.key));
      setMapping((current) => {
        const used = new Set(current.filter(Boolean) as MemberImportField[]);
        return current.map((cur, i) => {
          if (cur) return cur; // Auto-/Hand-Zuordnungen behalten — die KI füllt nur Lücken.
          const ai = res.mapping[i];
          if (!ai || !valid.has(ai) || used.has(ai as MemberImportField)) return null;
          used.add(ai as MemberImportField);
          return ai as MemberImportField;
        });
      });
      setAiMap({ pending: false, error: null, done: true });
    } catch (e) {
      setAiMap({
        pending: false,
        error: aiErrorMessage(e, 'ordne die Spalten unten von Hand zu.'),
        done: false,
      });
    }
  }

  function setColumn(index: number, field: MemberImportField | '') {
    setMapping((current) =>
      current.map((cur, i) => {
        if (i === index) return field === '' ? null : field;
        // Jedes Feld nur einmal: Auswahl an anderer Stelle löst die alte Zuordnung.
        if (field !== '' && cur === field) return null;
        return cur;
      }),
    );
  }

  function onFile(e: React.ChangeEvent<HTMLInputElement>) {
    const f = e.currentTarget.files?.[0];
    if (!f) return;
    const reader = new FileReader();
    reader.onload = () => setCsv(String(reader.result ?? ''));
    // UTF-8 explizit: Excel-Exporte mit Umlauten kommen sonst als Kauderwelsch an.
    reader.readAsText(f, 'utf-8');
    e.currentTarget.value = '';
  }

  // --- Freitext-Weg ---
  const [freetext, setFreetext] = useState('');
  const [textRows, setTextRows] = useState<MemberImportRow[]>([]);
  const [textAi, setTextAi] = useState<{ pending: boolean; error: string | null }>({
    pending: false,
    error: null,
  });

  async function runFreetext() {
    setTextAi({ pending: true, error: null });
    try {
      const res = await api.members.importFreetext(freetext.trim());
      setTextRows(res.rows);
      setTextAi({
        pending: false,
        error: res.rows.length === 0 ? 'Keine Kontakte im Text erkannt.' : null,
      });
    } catch (e) {
      setTextAi({
        pending: false,
        error: aiErrorMessage(e, 'trag die Kontakte über den CSV-Weg oder von Hand ein.'),
      });
    }
  }

  const rows = tab === 'csv' ? built.rows : textRows;
  const errors = tab === 'csv' ? built.errors : [];

  return (
    <Modal open={open} onClose={onClose} title="Kontakte importieren" size="lg">
      <div className="space-y-4">
        <div className="inline-flex rounded-lg border border-line p-0.5">
          {(
            [
              ['csv', 'CSV / Datei'],
              ['text', 'Freitext (KI)'],
            ] as const
          ).map(([key, label]) => (
            <button
              key={key}
              onClick={() => setTab(key)}
              className={cn(
                'rounded-md px-3 py-1.5 text-xs transition',
                tab === key ? 'bg-gold/15 text-gold-light' : 'text-text-muted hover:text-text',
              )}
            >
              {label}
            </button>
          ))}
        </div>

        {tab === 'csv' && (
          <>
            <div className="text-xs text-text-muted">
              CSV mit Kopfzeile — Exporte aus <span className="text-text-dim">HubSpot, Salesforce,
              Pipedrive, Outlook und Google Kontakte</span> werden automatisch erkannt. Bei allen
              anderen hilft die KI-Zuordnung, und jede Spalte lässt sich von Hand korrigieren.
            </div>
            <input
              type="file"
              accept=".csv,text/csv"
              onChange={onFile}
              className="block text-xs text-text-dim file:mr-3 file:rounded-md file:border-0 file:bg-raise-2 file:px-3 file:py-1.5 file:text-text hover:file:bg-raise-3"
            />
            <Textarea
              value={csv}
              onChange={(e) => setCsv(e.currentTarget.value)}
              placeholder="name,company,industry,email,city,tags&#10;Anna,Acme,KI,anna@acme.de,Berlin,KI|B2B"
              className="font-mono text-xs min-h-[120px]"
            />

            {table && (
              <div className="space-y-2">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <div className="text-xs text-text-muted">
                    Spalten-Zuordnung ({table.header.length} Spalten, Trennzeichen{' '}
                    <code className="text-gold-light">
                      {table.delimiter === '\t' ? 'Tab' : table.delimiter}
                    </code>
                    {unmappedCount > 0 && <> · {unmappedCount} nicht zugeordnet</>})
                  </div>
                  {unmappedCount > 0 && (
                    <Button
                      size="sm"
                      variant="secondary"
                      disabled={aiMap.pending}
                      onClick={() => void runAiMapping()}
                    >
                      {aiMap.pending ? 'KI ordnet zu…' : 'Mit KI zuordnen'}
                    </Button>
                  )}
                </div>
                {aiMap.error && <div className="text-xs text-danger">{aiMap.error}</div>}
                {aiMap.done && (
                  <div className="text-xs text-success">
                    KI-Zuordnung übernommen — bitte kurz prüfen.
                  </div>
                )}
                <div className="max-h-48 overflow-auto rounded-lg border border-line">
                  <table className="w-full text-xs">
                    <tbody>
                      {table.header.map((h, i) => (
                        <tr key={i} className="border-b border-line last:border-0">
                          <td className="px-3 py-1.5 text-text-dim whitespace-nowrap max-w-[180px] truncate" title={h}>
                            {h || <span className="text-text-muted">(Spalte {i + 1})</span>}
                          </td>
                          <td className="px-2 py-1 text-text-muted">→</td>
                          <td className="px-2 py-1 w-44">
                            <Select
                              value={mapping[i] ?? ''}
                              onChange={(e) => setColumn(i, e.currentTarget.value as MemberImportField | '')}
                              className="h-8 text-xs"
                            >
                              <option value="">— ignorieren —</option>
                              {MEMBER_IMPORT_FIELDS.map((f) => (
                                <option key={f.key} value={f.key}>
                                  {f.label}
                                </option>
                              ))}
                            </Select>
                          </td>
                          <td className="px-3 py-1.5 text-text-muted truncate max-w-[200px]" title={table.rows[0]?.[i] ?? ''}>
                            {table.rows[0]?.[i] ?? ''}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                {!hasNameMapping && (
                  <div className="rounded-lg border border-gold/30 bg-gold/10 p-3 text-xs text-text-dim">
                    Es ist noch keine Spalte als Name, Vorname oder Nachname zugeordnet — ohne
                    Namen kann nichts importiert werden.
                  </div>
                )}
              </div>
            )}
          </>
        )}

        {tab === 'text' && (
          <>
            <div className="text-xs text-text-muted">
              Text einfügen — E-Mail-Signaturen, kopierte Teilnehmerlisten, Notizen. Nex erkennt
              daraus Kontakte mit allen Angaben, die im Text stehen.
            </div>
            <Textarea
              value={freetext}
              onChange={(e) => setFreetext(e.currentTarget.value)}
              maxLength={20000}
              placeholder={'z.B.\n„Anna Meier, Geschäftsführerin der Acme GmbH, anna@acme.de, 0170 1234567, Berlin.\nThomas Weber vom Bau-Netzwerk Köln, macht Trockenbau, thomas@weber-bau.de …“'}
              className="text-xs min-h-[160px]"
            />
            <div className="flex items-center gap-3">
              <Button
                size="sm"
                disabled={textAi.pending || freetext.trim().length < 3}
                onClick={() => void runFreetext()}
              >
                {textAi.pending ? 'Nex liest…' : 'Kontakte erkennen lassen'}
              </Button>
              {textAi.error && <span className="text-xs text-danger">{textAi.error}</span>}
            </div>
          </>
        )}

        {errors.length > 0 && (
          <div className="rounded-lg border border-danger/30 bg-danger/10 p-3 text-xs text-text-dim space-y-1">
            {errors.slice(0, 6).map((e, i) => (
              <div key={i}>
                <span className="text-danger">Zeile {e.line}:</span> {e.message}
              </div>
            ))}
            {errors.length > 6 && (
              <div className="text-text-muted">… und {errors.length - 6} weitere.</div>
            )}
          </div>
        )}

        {rows.length > 0 && (
          <div>
            <div className="text-xs text-text-muted mb-2">Vorschau ({rows.length} Kontakte)</div>
            <div className="max-h-60 overflow-auto rounded-lg border border-line">
              <Table>
                <THead>
                  <TR>
                    <TH>Name</TH>
                    <TH>Firma</TH>
                    <TH>E-Mail</TH>
                    <TH>Telefon</TH>
                    <TH>Ort</TH>
                    <TH>Tags</TH>
                  </TR>
                </THead>
                <tbody>
                  {rows.slice(0, 50).map((r, i) => (
                    <TR key={i}>
                      <TD className="text-text whitespace-nowrap">{r.name}</TD>
                      <TD>{r.company ?? '—'}</TD>
                      <TD>{r.email ?? '—'}</TD>
                      <TD className="whitespace-nowrap">{r.phone ?? '—'}</TD>
                      <TD className="whitespace-nowrap">
                        {[r.zip, r.city].filter(Boolean).join(' ') || '—'}
                      </TD>
                      <TD>{r.tags?.join(', ') ?? '—'}</TD>
                    </TR>
                  ))}
                </tbody>
              </Table>
            </div>
            {rows.length > 50 && (
              <div className="mt-1 text-[11px] text-text-muted">
                Vorschau zeigt die ersten 50 — importiert werden alle {rows.length}.
              </div>
            )}
          </div>
        )}

        {result && (
          <div className="rounded-lg border border-line bg-raise p-3 text-xs text-text-dim">
            Importiert: <span className="text-gold">{result.created}</span> · Übersprungen (schon
            vorhanden): {result.skipped} · Fehler: {result.errors.length}
          </div>
        )}

        <div className="flex justify-end gap-2 pt-2">
          <Button variant="ghost" onClick={onClose}>
            Schließen
          </Button>
          <Button disabled={pending || rows.length === 0} onClick={() => onSubmit(rows)}>
            {pending
              ? 'Importiert…'
              : rows.length > 0
                ? `${rows.length} importieren`
                : 'Importieren'}
          </Button>
        </div>
      </div>
    </Modal>
  );
}
